/**
 * BarDrilldownCustomWidget — plain React/CSS drilldown bar chart, no
 * Highcharts. A from-scratch alternative to BarDrilldownWidget.tsx (which
 * hit Highcharts-integration issues: a duplicate context-button menu, and
 * the chart collapsing/not recovering when sibling content resizes its
 * container) — kept side by side so both can be compared directly.
 *
 * chart_type: "bar_drilldown_custom". Own drill-state stack (an array of
 * series ids) replaces Highcharts' native drilldown module; bars are plain
 * flexbox fills (same technique as InfoBarWidget's rate bars), so there's no
 * canvas/SVG sizing to keep in sync with the container — it just reflows
 * with normal CSS layout like any other DOM content.
 */

import { useMemo, useState } from "react";

import { toSafeNumber } from "src/charts/data/transformers";

type BarPoint = {
  x?: string;
  name?: string;
  y?: unknown;
  drilldown?: string;
};

type BarSeries = {
  name?: string;
  type?: string;
  data?: BarPoint[];
};

type DrilldownSeries = {
  id?: string;
  name?: string;
  type?: string;
  data?: (BarPoint | unknown[])[];
};

type Headline = { label?: string; value?: string; subtitle?: string };
type Status = { code?: string; label?: string; description?: string };
type TechnicalDetailRow = {
  measure?: string;
  cases?: number;
  definition?: string;
};

type Metadata = {
  headline?: Headline;
  status?: Status;
  technical_detail_rows?: TechnicalDetailRow[];
};

type BarDrilldownData = {
  series?: BarSeries[];
  drilldown?: { series?: DrilldownSeries[] };
};

type BarDrilldownCustomWidgetProps = {
  isLoading: boolean;
  data: unknown;
};

const PRIMARY_COLOR = "#2a78d6";
const CONTEXT_COLOR = "#94a3b8";

const STATUS_STYLES: Record<string, { color: string; icon: string }> = {
  good: { color: "#0ca30c", icon: "bi-check-circle-fill" },
  ok: { color: "#0ca30c", icon: "bi-check-circle-fill" },
  healthy: { color: "#0ca30c", icon: "bi-check-circle-fill" },
  warning: { color: "#fab219", icon: "bi-exclamation-triangle-fill" },
  no_new_data: { color: "#fab219", icon: "bi-exclamation-triangle-fill" },
  serious: { color: "#ec835a", icon: "bi-exclamation-circle-fill" },
  degraded: { color: "#ec835a", icon: "bi-exclamation-circle-fill" },
  critical: { color: "#d03b3b", icon: "bi-x-circle-fill" },
  error: { color: "#d03b3b", icon: "bi-x-circle-fill" },
};
const DEFAULT_STATUS_STYLE = STATUS_STYLES.warning!;

const unwrap = (
  raw: unknown,
): { data?: BarDrilldownData; metadata?: Metadata } => {
  if (!raw || typeof raw !== "object") return {};
  return raw as { data?: BarDrilldownData; metadata?: Metadata };
};

const isNumeric = (val: unknown): val is number =>
  typeof val === "number" && !isNaN(val);

const formatNum = (val: unknown): string =>
  isNumeric(val) ? val.toLocaleString() : String(val ?? "–");

const isContextPoint = (name: string) => /^total\b/i.test(name.trim());

type ResolvedPoint = {
  key: string;
  name: string;
  y: number;
  drilldownId?: string;
};

function resolvePoints(
  raw: (BarPoint | unknown[])[] | undefined,
  drilldownSeriesList: DrilldownSeries[],
): ResolvedPoint[] {
  return (raw ?? []).map((point, index) => {
    if (Array.isArray(point)) {
      return {
        key: String(point[0] ?? index),
        name: String(point[0] ?? ""),
        y: toSafeNumber(point[1]),
      };
    }
    const p = point as BarPoint;
    const name = String(p.x ?? p.name ?? "");
    const target = p.drilldown
      ? drilldownSeriesList.find((series) => series.id === p.drilldown)
      : undefined;
    const hasDrilldown = !!target && (target.data?.length ?? 0) > 0;
    return {
      key: name || String(index),
      name,
      y: toSafeNumber(p.y),
      ...(hasDrilldown ? { drilldownId: p.drilldown } : {}),
    };
  });
}

export default function BarDrilldownCustomWidget({
  isLoading,
  data,
}: BarDrilldownCustomWidgetProps) {
  const { data: chartData, metadata } = useMemo(() => unwrap(data), [data]);
  const drilldownSeriesList = useMemo(
    () => chartData?.drilldown?.series ?? [],
    [chartData],
  );

  // The navigation stack — each entry is a series id drilled into so far.
  // Empty = showing the top-level series.
  const [drillPath, setDrillPath] = useState<string[]>([]);
  const [showDetails, setShowDetails] = useState(false);

  const topSeries = chartData?.series?.[0];
  const topPoints = useMemo(
    () => resolvePoints(topSeries?.data, drilldownSeriesList),
    [topSeries, drilldownSeriesList],
  );

  const levels = useMemo(
    () =>
      drillPath.map(
        (id) => drilldownSeriesList.find((series) => series.id === id),
      ),
    [drillPath, drilldownSeriesList],
  );

  const currentLevelSeries = levels[levels.length - 1];
  const currentPoints = currentLevelSeries
    ? resolvePoints(currentLevelSeries.data, drilldownSeriesList)
    : topPoints;
  const currentLevelName =
    levels.length > 0
      ? (currentLevelSeries?.name ?? currentLevelSeries?.id ?? "")
      : (topSeries?.name ?? "");

  const maxY = Math.max(1, ...currentPoints.map((point) => point.y));

  const drillInto = (id: string) => setDrillPath((path) => [...path, id]);
  const goToDepth = (depth: number) =>
    setDrillPath((path) => path.slice(0, depth));

  if (isLoading) {
    return (
      <div className="placeholder-glow h-100 w-100">
        <span className="placeholder w-100 h-100" />
      </div>
    );
  }

  const headline = metadata?.headline;
  const status = metadata?.status;
  const statusStyle = status?.code
    ? (STATUS_STYLES[status.code] ?? DEFAULT_STATUS_STYLE)
    : null;
  const detailRows = metadata?.technical_detail_rows ?? [];

  return (
    <div className="bar-drilldown-custom">
      {headline && (
        <div className="bar-drilldown-custom__headline">
          {headline.label && (
            <span className="bar-drilldown-custom__headline-label">
              {headline.label}
            </span>
          )}
          {headline.value && (
            <span className="bar-drilldown-custom__headline-value">
              {headline.value}
            </span>
          )}
          {headline.subtitle && (
            <span className="bar-drilldown-custom__headline-subtitle">
              {headline.subtitle}
            </span>
          )}
        </div>
      )}

      {status && statusStyle && (
        <div className="bar-drilldown-custom__status" role="status">
          <i
            className={`bi ${statusStyle.icon}`}
            style={{ color: statusStyle.color }}
            aria-hidden="true"
          />
          <div className="bar-drilldown-custom__status-text">
            {status.label && (
              <span className="bar-drilldown-custom__status-label">
                {status.label}
              </span>
            )}
            {status.description && (
              <span className="bar-drilldown-custom__status-desc">
                {status.description}
              </span>
            )}
          </div>
        </div>
      )}

      {topPoints.length ? (
        <>
          {drillPath.length > 0 && (
            <nav
              className="bar-drilldown-custom__breadcrumb"
              aria-label="Drilldown path"
            >
              <button type="button" onClick={() => goToDepth(0)}>
                {topSeries?.name ?? "All"}
              </button>
              {levels.map((level, index) => (
                <span key={level?.id ?? index}>
                  <i className="bi bi-chevron-right" aria-hidden="true" />
                  <button type="button" onClick={() => goToDepth(index + 1)}>
                    {level?.name ?? level?.id ?? ""}
                  </button>
                </span>
              ))}
            </nav>
          )}

          <div className="bar-drilldown-custom__level">
            {currentPoints.map((point) => {
              const color = isContextPoint(point.name)
                ? CONTEXT_COLOR
                : PRIMARY_COLOR;
              const widthPct = Math.max(2, (point.y / maxY) * 100);
              return (
                <button
                  key={point.key}
                  type="button"
                  className="bar-drilldown-custom__row"
                  disabled={!point.drilldownId}
                  onClick={() =>
                    point.drilldownId && drillInto(point.drilldownId)
                  }
                >
                  <span className="bar-drilldown-custom__label">
                    {point.name}
                  </span>
                  <span className="bar-drilldown-custom__track">
                    <span
                      className="bar-drilldown-custom__fill"
                      style={{ width: `${widthPct}%`, background: color }}
                    />
                  </span>
                  <span className="bar-drilldown-custom__value">
                    {formatNum(point.y)}
                  </span>
                  {point.drilldownId && (
                    <i
                      className="bi bi-chevron-right bar-drilldown-custom__chevron"
                      aria-hidden="true"
                    />
                  )}
                </button>
              );
            })}
          </div>

          {levels.length > 0 && currentPoints.length === 0 && (
            <p className="text-muted small mb-0">
              No breakdown available for {currentLevelName}.
            </p>
          )}
        </>
      ) : (
        <p className="text-muted small mb-0">No data available.</p>
      )}

      {detailRows.length > 0 && (
        <div className="bar-drilldown-custom__details">
          <button
            type="button"
            className="bar-drilldown-custom__details-toggle"
            aria-expanded={showDetails}
            onClick={() => setShowDetails((value) => !value)}
          >
            <i
              className={`bi bi-chevron-${showDetails ? "down" : "right"}`}
              aria-hidden="true"
            />
            <span>Technical details</span>
          </button>

          {showDetails && (
            <table className="bar-drilldown-custom__details-table">
              <thead>
                <tr>
                  <th>Measure</th>
                  <th>Cases</th>
                  <th>Definition</th>
                </tr>
              </thead>
              <tbody>
                {detailRows.map((row, index) => (
                  <tr key={row.measure ?? index}>
                    <td>{row.measure ?? "—"}</td>
                    <td>{formatNum(row.cases)}</td>
                    <td>{row.definition ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
